
import { useState, useCallback } from "react";

export type ScanStatus = 
  "idle" | "initializing" | "searching" | "detecting" | "capturing" | "processing" | "success" | "error";

interface ScanState {
  status: ScanStatus;
  message: string;
  progress?: number;
  cardDetected: boolean;
  cardPosition: { x: number; y: number; width: number; height: number } | null;
  imageUrl: string | null;
  cardData: any | null;
  errorMessage: string | null;
}

export const useScanState = () => {
  const [state, setState] = useState<ScanState>({
    status: "idle",
    message: "Ready to scan",
    cardDetected: false,
    cardPosition: null,
    imageUrl: null,
    cardData: null,
    errorMessage: null
  });
  
  const updateStatus = useCallback((
    status: ScanStatus, 
    message: string, 
    additionalState: Partial<ScanState> = {}
  ) => {
    setState(prev => ({
      ...prev,
      status,
      message,
      ...additionalState
    }));
    
    // Log status changes for better debug visibility
    console.log(`Scan status updated: ${status} - ${message}`);
  }, []);
  
  const setCardDetected = useCallback((
    detected: boolean, 
    position: { x: number; y: number; width: number; height: number } | null = null
  ) => {
    setState(prev => {
      // Only log if detection state changes
      if (prev.cardDetected !== detected) {
        console.log(`Card detection changed: ${detected ? 'DETECTED' : 'LOST'}`);
      }
      
      return {
        ...prev,
        cardDetected: detected,
        cardPosition: position,
        status: detected ? "detecting" : prev.status === "detecting" ? "searching" : prev.status,
        message: detected ? "Card detected - hold still..." : "Center your card in the frame..."
      };
    });
  }, []);
  
  const startScanning = useCallback(() => {
    console.log("Scanner starting...");
    setState({
      status: "initializing",
      message: "Initializing scanner...",
      cardDetected: false,
      cardPosition: null,
      imageUrl: null,
      cardData: null,
      errorMessage: null
    });
    
    // Automatically transition to searching state after a short delay
    setTimeout(() => {
      setState(prev => {
        // Only update if we're still in initializing state
        if (prev.status === "initializing") {
          console.log("Scanner ready, searching for cards...");
          return {
            ...prev,
            status: "searching",
            message: "Position your card in the center of the frame..."
          };
        }
        return prev;
      });
    }, 1500);
  }, []);
  
  const setImageCapture = useCallback((imageUrl: string) => {
    console.log("Card image captured, preparing for processing");
    setState(prev => ({
      ...prev,
      imageUrl,
      status: "capturing",
      message: "Card captured! Processing..."
    }));
    
    // Automatically transition to processing state after a short delay
    setTimeout(() => {
      setState(prev => {
        // Only update if we're still in capturing state
        if (prev.status === "capturing") {
          console.log("Beginning card analysis...");
          return {
            ...prev,
            status: "processing",
            message: "Analyzing your Pokémon card..."
          };
        }
        return prev;
      });
    }, 800);
  }, []);
  
  const setProcessingProgress = useCallback((progress: number) => {
    console.log(`Processing progress: ${Math.round(progress)}%`);
    setState(prev => ({
      ...prev,
      progress,
      message: progress < 50 
        ? "Analyzing card details..." 
        : "Looking up current market value..."
    }));
  }, []);
  
  const setScanSuccess = useCallback((cardData: any) => {
    console.log("Scan complete! Card identified successfully");
    setState(prev => ({
      ...prev,
      cardData,
      status: "success",
      message: "Card identified successfully!"
    }));
  }, []);
  
  const setScanError = useCallback((errorMessage: string) => {
    console.error("Scan failed:", errorMessage);
    setState(prev => ({
      ...prev,
      status: "error",
      message: "Scan failed",
      errorMessage
    }));
  }, []);
  
  const resetScan = useCallback(() => {
    console.log("Scanner reset");
    setState({
      status: "idle",
      message: "Ready to scan",
      cardDetected: false,
      cardPosition: null,
      imageUrl: null,
      cardData: null,
      errorMessage: null
    });
  }, []);
  
  return {
    state,
    updateStatus,
    setCardDetected,
    startScanning,
    setImageCapture,
    setProcessingProgress,
    setScanSuccess,
    setScanError,
    resetScan
  };
};
